import {
  isTutorialMissionKey,
  type TutorialMissionKey,
} from "@/constants/tutorialMissions";

/**
 * 「やってみる」で始めたチュートリアルミッション (案内を出している最中のもの) の状態。
 *
 * 画面を移っても案内が出続けるよう sessionStorage に置く (タブを閉じれば消える)。
 * manualIndex は、画面から進み具合を判断できない手順 (ホーム画面に追加など) を
 * 「次へ」「戻る」で選んだ位置。
 *
 * ブラウザ API に直接触れず storage を引数で受け取るのは、単体テストで差し替えるため
 * (homeTour.ts と同じ形)。
 */

export const ACTIVE_MISSION_EVENT = "gc:tutorial:active-changed";
const ACTIVE_KEY = "gc:tutorial:active";

export type TutorialStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface ActiveMission {
  key: TutorialMissionKey;
  manualIndex: number;
}

export function readActiveMission(storage: TutorialStorage | null): ActiveMission | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(ACTIVE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const { key, manualIndex } = parsed as Record<string, unknown>;
    if (typeof key !== "string" || !isTutorialMissionKey(key)) return null;
    return {
      key,
      manualIndex:
        typeof manualIndex === "number" && Number.isInteger(manualIndex) && manualIndex >= 0
          ? manualIndex
          : 0,
    };
  } catch {
    return null;
  }
}

/** 案内を始める / 手順を変える (null で案内をやめる) */
export function writeActiveMission(
  storage: TutorialStorage | null,
  state: ActiveMission | null,
): void {
  if (storage) {
    try {
      if (state) storage.setItem(ACTIVE_KEY, JSON.stringify(state));
      else storage.removeItem(ACTIVE_KEY);
    } catch {
      // 保存できない環境では、画面を移ると案内が消えるだけ (同じ画面では下の通知で出る)
    }
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(ACTIVE_MISSION_EVENT, { detail: state }));
  }
}

// ── 「？」メニューから、ホームのミッションの「はじめて」タブを開く ─────────────

export const MISSION_TAB_REQUEST_EVENT = "gc:tutorial:open-tab";
const TAB_REQUEST_KEY = "gc:tutorial:open-tab";

/** ホームへ移動する前に呼ぶ。ホーム側が consumeMissionTabRequest で受け取る */
export function requestTutorialTab(storage: TutorialStorage | null): void {
  if (!storage) return;
  try {
    storage.setItem(TAB_REQUEST_KEY, "1");
  } catch {
    // 保存できない環境では、ホームにいるときの即時イベントだけが効く
  }
}

/** 依頼があれば取り消したうえで true を返す (1 回の依頼で 1 回だけ) */
export function consumeTutorialTabRequest(storage: TutorialStorage | null): boolean {
  if (!storage) return false;
  try {
    const requested = storage.getItem(TAB_REQUEST_KEY) === "1";
    if (requested) storage.removeItem(TAB_REQUEST_KEY);
    return requested;
  } catch {
    return false;
  }
}

// ── ミッションのタブ (はじめて / デイリー / ...) の最後の選択 ─────────────

const MISSION_TAB_KEY = "gc:missionTab";

export type MissionTabKey = "tutorial" | "daily" | "weekly" | "monthly";

const MISSION_TABS: readonly MissionTabKey[] = ["tutorial", "daily", "weekly", "monthly"];

export function readMissionTab(storage: TutorialStorage | null): MissionTabKey | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(MISSION_TAB_KEY);
    return MISSION_TABS.find((t) => t === raw) ?? null;
  } catch {
    return null;
  }
}

export function writeMissionTab(storage: TutorialStorage | null, tab: MissionTabKey): void {
  if (!storage) return;
  try {
    storage.setItem(MISSION_TAB_KEY, tab);
  } catch {
    // 保存できなくても、次に開いたときに既定のタブになるだけ
  }
}

export function getBrowserStorage(kind: "local" | "session"): Storage | null {
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}
